import { env } from './env'
import { clearToken, getRefreshToken, getToken, setRefreshToken, setToken } from './storage'
import type { RequestOptions, Transport } from '@romain130492/topchopsticks'

export class ApiError extends Error {
  status: number
  data: unknown
  constructor(status: number, message: string, data: unknown) {
    super(message)
    this.status = status
    this.data = data
  }
}

const buildQuery = (query?: RequestOptions['query']) => {
  if (!query) return ''
  const parts = Object.entries(query)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
  return parts.length ? `?${parts.join('&')}` : ''
}

/**
 * Renew the access token, at most once at a time.
 *
 * SINGLE-FLIGHT. A screen typically fires several requests together (profile,
 * application, events). When the access token expires they ALL get 401 at
 * once, and without this they would each spend a refresh token — but refresh
 * tokens rotate and are single-use, so the first would succeed and the rest
 * would present an already-spent token and be treated as theft. Sharing one
 * in-flight promise is what makes rotation safe on a real page.
 *
 * @returns the new access token, or '' when the session is genuinely over.
 */
let refreshInFlight: Promise<string> | null = null

const refreshAccessToken = (): Promise<string> => {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    const refreshToken = getRefreshToken()
    if (!refreshToken) return ''
    try {
      const response = await fetch(`${env.apiUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken })
      })
      if (!response.ok) {
        // The refresh token itself was rejected — that is a real sign-out.
        // Anything else (server down) leaves the session alone to retry later.
        if (response.status === 401 || response.status === 403) clearToken()
        return ''
      }
      const data = (await response.json()) as { token?: string; refresh_token?: string }
      if (!data.token) return ''
      setToken(data.token)
      // The presented refresh token is now dead server-side. Storing the
      // replacement is not optional — miss it and the next refresh fails.
      if (data.refresh_token) setRefreshToken(data.refresh_token)
      return data.token
    } catch {
      // Network failure: say nothing, keep the tokens, let the caller surface
      // the original error.
      return ''
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

/**
 * The web transport. Same paths, same Bearer JWT, same error shape as the
 * mini program's services/request.js — the backend cannot tell the two apart,
 * which is exactly what we want.
 *
 * On 401 it refreshes and retries ONCE, transparently, so an expired access
 * token is invisible rather than a logout.
 */
export const request: Transport = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const { method = 'GET', query, body } = options

  const send = (token: string) =>
    fetch(`${env.apiUrl}${path}${buildQuery(query)}`, {
      method,
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: body ? JSON.stringify(body) : undefined
    })

  let response = await send(getToken())

  // Never try to refresh the refresh call itself — that is an infinite loop.
  if (response.status === 401 && !path.startsWith('/auth/')) {
    const renewed = await refreshAccessToken()
    if (renewed) response = await send(renewed)
  }

  const text = await response.text()
  let data: unknown = null
  try { data = text ? JSON.parse(text) : null } catch { data = text }

  if (!response.ok) {
    // Still 401 after a refresh attempt = the session is genuinely over.
    // 404 on /me = the token is valid but points at a profile that no longer
    // exists (wiped database, deleted account). Both mean sign out.
    if (response.status === 401 || (response.status === 404 && path === '/me')) clearToken()
    const message =
      (data as { message?: string } | null)?.message || `${response.status} ${response.statusText}`
    throw new ApiError(response.status, message, data)
  }
  return data as T
}
